import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { FileText, AlertCircle, Scale, Ban, CheckCircle, Mail, Phone } from 'lucide-react';
import { businessInfo } from '../data/mock';

const TermsOfService = () => {
  const sections = [
    {
      icon: FileText,
      title: "Service Agreement",
      content: [
        "By requesting our tipper hire services, you agree to:",
        "• Provide accurate project information and site details",
        "• Ensure safe access to the delivery/pickup location",
        "• Be present or have an authorized representative on-site",
        "• Pay for services as per the agreed quote and terms",
        "• Notify us immediately of any changes to your requirements"
      ]
    },
    {
      icon: CheckCircle,
      title: "Service Provision",
      content: [
        "Adelaide Hills Tipper Hire commits to:",
        "• Provide professional tipper hire services as agreed",
        "• Maintain well-serviced and safe equipment",
        "• Employ experienced and qualified operators",
        "• Deliver services in a timely and efficient manner",
        "• Comply with all relevant safety and environmental regulations"
      ]
    },
    {
      icon: Scale,
      title: "Pricing & Payment",
      content: [
        "• Quotes are valid for 30 days from the date of issue",
        "• Prices may vary based on material type, distance, and site conditions",
        "• Payment is due 14 days after completion of work unless otherwise specified",
        "• We accept cash, bank transfer, and approved account customers",
        "• Late payment may incur additional charges",
        "• All prices are exclusive of GST"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cancellations & Changes",
      content: [
        "• Cancellations must be made at least 24 hours before scheduled service",
        "• Late cancellations (less than 24 hours) may incur a cancellation fee",
        "• We reserve the right to reschedule due to weather or equipment issues",
        "• Changes to booking details should be communicated as soon as possible",
        "• Cancellation fees help cover operational costs and lost booking opportunities"
      ]
    },
    {
      icon: Ban,
      title: "Liability & Insurance",
      content: [
        "• Adelaide Hills Tipper Hire maintains comprehensive insurance coverage",
        "• We are not liable for damage to underground services not clearly marked",
        "• Customers are responsible for ensuring site access is safe and suitable",
        "• We are not liable for delays caused by circumstances beyond our control",
        "• Damage to property caused by customer-provided inaccurate information is not our responsibility",
        "• Claims must be reported within 48 hours of service completion"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section className="relative py-20 bg-zinc-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/50 mb-4">Legal</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-300">
              Last updated: December 2025
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-zinc-800 border-zinc-700 mb-8">
              <CardContent className="p-8">
                <p className="text-gray-300 leading-relaxed mb-4">
                  Welcome to Adelaide Hills Tipper Hire. These Terms of Service ("Terms") govern your use of our website and 
                  tipper hire services. Please read these terms carefully before using our services.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  By accessing our website or using our services, you agree to be bound by these Terms. If you disagree with 
                  any part of these terms, please do not use our services.
                </p>
              </CardContent>
            </Card>

            {/* Main Sections */}
            <div className="space-y-6">
              {sections.map((section, index) => (
                <Card key={index} className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <section.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-white font-bold text-2xl mb-4">{section.title}</h2>
                        <div className="space-y-2">
                          {section.content.map((line, idx) => (
                            <p key={idx} className="text-gray-300 leading-relaxed">
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Site Access & Safety */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Site Access & Safety Requirements</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Customers must ensure:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li>• Safe and adequate access for our vehicles (minimum 3.5m width, 4.5m height clearance)</li>
                  <li>• All underground services are clearly marked and communicated</li>
                  <li>• The site is free from hazards that could affect our operations</li>
                  <li>• Appropriate permits and approvals are obtained where required</li>
                  <li>• Overhead power lines and other obstacles are identified</li>
                  <li>• Ground conditions can support the weight of loaded vehicles</li>
                </ul>
              </CardContent>
            </Card>

            {/* Environmental Responsibility */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Environmental Responsibility</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We are committed to environmental responsibility:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li>• All waste materials are disposed of at licensed facilities</li>
                  <li>• We comply with all environmental regulations</li>
                  <li>• Customers must not request disposal of hazardous materials</li>
                  <li>• We reserve the right to refuse loads containing prohibited materials</li>
                </ul>
              </CardContent>
            </Card>

            {/* Dispute Resolution */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Dispute Resolution</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  In the event of a dispute:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li>• Contact us immediately to discuss the issue</li>
                  <li>• We will work in good faith to resolve disputes promptly</li>
                  <li>• If resolution cannot be reached, disputes will be governed by South Australian law</li>
                  <li>• Both parties agree to attempt mediation before legal proceedings</li>
                </ul>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Changes to Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting 
                  to this page. Your continued use of our services after changes are posted constitutes acceptance of the 
                  modified Terms.
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-gradient-to-br from-orange-600 to-orange-500 border-0 mt-8">
              <CardContent className="p-8 text-center">
                <h2 className="text-white font-bold text-2xl mb-4">Questions About Our Terms?</h2>
                <p className="text-white/90 mb-6">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="flex flex-wrap justify-center gap-6 text-white">
                  <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                    <Mail className="w-5 h-5" />
                    {businessInfo.email}
                  </a>
                  <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                    <Phone className="w-5 h-5" />
                    {businessInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
